import { useUserSettings } from "@/contexts"
import { useCurrencySymbol } from "@/hooks/useCurrencySymbol/useCurrencySymbol"
import { ApiQueryName } from "@/models/api"
import type { CurrencyCode } from "@/models/currencyCode"
import type { Organization } from "@/models/organization"
import type { Site } from "@/models/site"
import { httpClient } from "@/utils/api"
import { useQuery } from "@tanstack/react-query"
import { omitBy } from "lodash-es"

import { useAuthorizedOrganizations } from "../organization/useAuthorizedOrganizations"
import { useRenameQueryResult } from "../utils"
import { createQueryKey } from "../utils/createQueryKey"
import { getSafeSigmaEmbedUserEmail } from "./sigma-utils"
import {
  allCustomersTeamName,
  allFinancialTeamName,
  allQATeamName,
  allSitesTeamName,
  consultantTeamNames,
} from "./sigma.types"
import type {
  SigmaTeam,
  SigmaWorkbook,
  UseSigmaServiceOptions,
} from "./sigma.types"
import { useSigmaTeams } from "./use-sigma-teams"

interface FetchSigmaWorkbookEmbedUrlConfig {
  currency: CurrencyCode
  email: string
  lng: string
  organizationId: Organization["id"]
  siteId: Site["id"]
  teams: SigmaTeam["name"][]
  unitsSystem: string
  userAttributes: Record<string, string>
  workbookUrlId: SigmaWorkbook["workbookUrlId"]
}

interface UseSigmaWorkbookEmbedUrlConfig {
  email: string | undefined
  lng: string
  organizationId: Organization["id"]
  siteId: Site["id"]
  workbookUrlId: SigmaWorkbook["workbookUrlId"] | undefined
}

const fetchSigmaWorkbookSecureEmbedUrl = async (
  config: FetchSigmaWorkbookEmbedUrlConfig
): Promise<string> => {
  return httpClient
    .get<string>(`/api/v1/sigma_workbook_embed_jwt`, {
      params: {
        email: config.email,
        teams: config.teams,
        user_attributes: omitBy(
          config.userAttributes,
          (value) => value === "" || value === null || value === undefined
        ),
      },
    })
    .then((response) => {
      const urlParams = new URLSearchParams()

      if (config.organizationId) {
        // Organization control
        urlParams.set("Organization", config.organizationId)
      }

      if (config.siteId) {
        // Site control
        urlParams.set("Site", config.siteId)
      }

      urlParams.set(":embed", "true") // Hides top primary menu bar
      urlParams.set(":hide_folder_navigation", "true") // Prevent workspace and other folder navigation
      urlParams.set(":menu_position", "bottom") // Show and position the secondary menu bar at the bottom
      urlParams.set(":jwt", encodeURIComponent(response.data))
      urlParams.set(":lng", config.lng)
      // Set control values
      urlParams.set("Language", config.lng)
      urlParams.set("Units", config.unitsSystem)
      urlParams.set("Currency", config.currency)

      const url = `https://app.sigmacomputing.com/n-zero1/workbook/${config.workbookUrlId}?${urlParams.toString()}`

      return url
    })
}

/**
 * Gets an embed url for the requested Sigma workbook
 *
 * @returns The Sigma workbook embed url
 *
 * @example
 * const {
 *   sigmaWorkbookSecureEmbedUrlData,
 * } = useSigmaWorkbookSecureEmbedUrl({
 *   lng: "ja",
 *   organizationId: "8",
 *   team: "City of Reno",
 *   workbookId: "1234567890",
 * })
 */
export const useSigmaWorkbookEmbedUrl = (
  serviceOptions: UseSigmaServiceOptions<UseSigmaWorkbookEmbedUrlConfig> = {}
) => {
  const {
    options = {
      email: "",
      lng: "",
      organizationId: "",
      siteId: undefined,
      workbookUrlId: "",
    },
    queryOptions = {},
  } = serviceOptions || {}

  const { authorizedOrganizationsData } = useAuthorizedOrganizations()
  const currencySymbol = useCurrencySymbol()
  const { sigmaTeamsData, sigmaTeamsIsFetched } = useSigmaTeams()
  const { currency, unitsSystem } = useUserSettings()

  const fetchConfig: FetchSigmaWorkbookEmbedUrlConfig = {
    currency,
    email: getSafeSigmaEmbedUserEmail(options.email),
    lng: options.lng,
    organizationId: options.organizationId,
    siteId: options.siteId,
    // Teams must be consistent across calls because resulting JWT is persistent
    teams: [
      ...consultantTeamNames,
      allCustomersTeamName,
      allFinancialTeamName,
      allQATeamName,
      allSitesTeamName,
      ...(authorizedOrganizationsData
        ?.map((organization) => organization.name)
        ?.filter((organizationName) =>
          sigmaTeamsData?.entries.find((team) => team.name === organizationName)
        ) ?? []),
    ],
    unitsSystem,
    // User attributes should be consistent across calls because resulting JWT is persistent
    // and can affect async events like scheduled exports
    // Need to make it consistent in the future
    userAttributes: {
      currency_symbol: currencySymbol, // Eventually remove this user attribute after we're using the lng url param
      organization_id: options.organizationId ?? "", // Eventually pass in all authorized organizations after we're using the Organization control url param
      site_id: options.siteId ?? "", // Eventually remove this user attribute after we're using the Site control url param
    },
    workbookUrlId: options.workbookUrlId ?? "",
  }

  // Explicitly type as boolean since queryOptions.enabled could be a function in v5
  const enabled: boolean =
    Boolean(fetchConfig.email) &&
    Boolean(fetchConfig.lng) &&
    Boolean(fetchConfig.organizationId) &&
    sigmaTeamsIsFetched &&
    Boolean(fetchConfig.teams.length) &&
    Boolean(fetchConfig.workbookUrlId) &&
    queryOptions.enabled !== false

  const queryKey = createQueryKey(
    ApiQueryName.SigmaWorkbookEmbedUrl,
    "getOne",
    null,
    options
  )
  const queryResult = useQuery({
    // We never want to cache the url because it can only be used once
    gcTime: 0,
    enabled,
    queryKey,
    queryFn: (): Promise<string> =>
      fetchSigmaWorkbookSecureEmbedUrl(fetchConfig),
    // We don't want to fetch a new url while on the page
    staleTime: Infinity,
  })

  return useRenameQueryResult(queryResult, ApiQueryName.SigmaWorkbookEmbedUrl)
}
